import { prefersReducedMotion, useReveal } from "../lib/useReveal";

interface Layer {
  label: string;
  copy: string;
  radius: number;
  color: string;
}

const LAYERS: Layer[] = [
  { label: "Edge & network", copy: "WAF, DDoS mitigation, and edge filtering before traffic ever reaches an origin.", radius: 150, color: "#67d3ff" },
  { label: "Application", copy: "Reviewed code, dependency hygiene, and secrets kept out of the request path.", radius: 116, color: "#4d97ff" },
  { label: "Identity", copy: "Zero Trust access, least privilege, and verification on every request.", radius: 82, color: "#3a82ff" },
  { label: "Data", copy: "Encryption, tenant isolation, and the smallest viable exposure surface.", radius: 48, color: "#2d6cf6" },
];

export function ZeroTrustRings(): React.ReactElement {
  const { ref, shown } = useReveal(0.3);
  const reduced = prefersReducedMotion();

  return (
    <div ref={ref} className={`viz viz-rings${shown ? " is-shown" : ""}`}>
      <div className="rings-stage">
        <svg viewBox="0 0 360 360" role="img" aria-label="Defense in depth: edge, application, identity and data layers around a protected core.">
          {LAYERS.map((layer, index) => {
            const circumference = 2 * Math.PI * layer.radius;
            return (
              <circle
                key={layer.label}
                cx="180"
                cy="180"
                r={layer.radius}
                className="ring-circle"
                style={{
                  stroke: layer.color,
                  strokeDasharray: circumference,
                  strokeDashoffset: shown ? 0 : circumference,
                  transitionDelay: `${index * 160}ms`,
                }}
              />
            );
          })}
          {shown && !reduced && (
            <g className="ring-sweep">
              <line x1="180" y1="180" x2="180" y2="42" />
            </g>
          )}
          <circle cx="180" cy="180" r="14" className="ring-core" />
        </svg>
      </div>
      <ol className="rings-legend">
        {LAYERS.map((layer) => (
          <li key={layer.label} style={{ "--dot": layer.color } as React.CSSProperties}>
            <span className="rings-dot" aria-hidden="true" />
            <div>
              <strong>{layer.label}</strong>
              <p>{layer.copy}</p>
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}
